// The browser receives only Paddle's public client token, never the API key.
interface PaddleEvent {name: string; data?: {transaction_id?: string}; error?: {detail?: string}}
interface PaddleCustomer {id?: string}
interface PaddleClient {
  Environment: {set: (environment: 'sandbox') => void};
  Initialize: (options: {token: string; pwCustomer: PaddleCustomer; eventCallback: (event: PaddleEvent) => void}) => void;
  Update: (options: {pwCustomer: PaddleCustomer}) => void;
  Checkout: {open: (options: Record<string, unknown>) => void};
}
declare global {interface Window {Paddle?: PaddleClient}}
let initialized: Promise<void> | undefined;
let configuration: string | undefined;
let listener: ((event: PaddleEvent) => void) | undefined;
function retainCustomer(mode: string, id?: string | null): PaddleCustomer {
  if (mode !== 'live' || !id) return {};
  if (!/^ctm_[a-z0-9]{26}$/.test(id)) throw new Error('Invalid customer identity.');
  return {id};
}
export function initializePaddle(token: string, mode: string, customerId?: string | null): Promise<void> {
  if (!['sandbox','live'].includes(mode) || !token.startsWith(mode === 'live' ? 'live_' : 'test_')) return Promise.reject(new Error('Invalid payment configuration.'));
  const customer = retainCustomer(mode, customerId);
  const key = `${mode}:${token}`;
  if (initialized) {
    if (configuration !== key) return Promise.reject(new Error('Payment environment changed. Reload this page.'));
    return initialized.then(() => {window.Paddle!.Update({pwCustomer: customer});});
  }
  configuration = key;
  initialized = new Promise<void>((resolve, reject) => {
    const start = () => {
      if (!window.Paddle) {reject(new Error('Unable to load payment checkout.'));return;}
      // Sandbox remains supported in DEV; Live uses Paddle's default environment.
      if (mode === 'sandbox') window.Paddle.Environment.set('sandbox');
      window.Paddle.Initialize({token, pwCustomer: customer, eventCallback: event => {
        if (event.name === 'checkout.completed') window.dispatchEvent(new CustomEvent('paddlePaymentCompleted', {detail:event.data?.transaction_id}));
        listener?.(event);
      }});
      window.addEventListener('logout', () => window.Paddle?.Update({pwCustomer: {}}));
      resolve();
    };
    if (window.Paddle) {start();return;}
    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';script.async = true;
    script.onload=start;script.onerror=()=>{script.remove();initialized=undefined;configuration=undefined;reject(new Error('Unable to load payment checkout.'));};
    document.head.appendChild(script);
  });
  return initialized;
}
export async function openPaddle(token: string, mode: string, options: Record<string, unknown>, customerId?: string | null): Promise<string | null> {
  await initializePaddle(token, mode, customerId);
  if (listener) throw new Error('A checkout is already open.');
  return new Promise((resolve,reject)=>{
    listener=event=>{
      if (event.name==='checkout.completed') {listener=undefined;resolve(event.data?.transaction_id || null);}
      else if (event.name==='checkout.closed') {listener=undefined;resolve(null);}
      else if (event.name==='checkout.error') {listener=undefined;reject(new Error('Could not complete this checkout. Please try again.'));}
    };
    try {window.Paddle!.Checkout.open({...options,settings:{displayMode:'overlay',allowLogout:false,showAddDiscounts:false}});}
    catch(e) {listener=undefined;reject(e);}
  });
}
